/* eslint-disable no-use-before-define */
import { Chip, Stack, Typography } from '@mui/material';
import { UseFormReturn, useFormContext } from 'react-hook-form';
import { mapCountry, mapIndustry, mapProjectSize, mapService } from 'tdc-web-backend/enums/enums';
import { useTranslation } from 'react-i18next';
import { formatDateFns } from '../../../../utils/helpers';
import BaseInputLabel from '../../../../components/custom-inputs/BaseInputLabel';
import { primaryDark } from '../../../../utils/color';
import { mapTechLanguage, mapTechPlatform, mapTechTool } from '../../../../utils/enums';

interface ChipGroupProps {
  label: string;
  choices: Map<string, string>;
  name: string;
  translation?: {
    t: (key: string, options?: Record<string, unknown>) => string;
    ns?: string;
  };
}

const RightColumnRenderComponent = ({ form }: { form: UseFormReturn }) => {
  const { t, i18n } = useTranslation();
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        p={1.5}
        justifyContent="space-between"
        sx={{ backgroundColor: 'primaryDark.200', borderRadius: '3px' }}
      >
        <Typography variant="heading5" color="primaryDark.500">
          {t('common.created')}
        </Typography>
        <Typography variant="heading5" color="primaryDark.600">
          {formatDateFns(new Date(), i18n.language)}
        </Typography>
      </Stack>
      <ChipGroup
        label={t('caseStudies.clientIndustry')}
        name="details.industries"
        choices={mapIndustry}
      />
      <ChipGroup
        label={t('settings.companyProfile.services')}
        name="details.services"
        choices={mapService}
      />

      {form.getValues('details.platforms').length !== 0 && (
        <ChipGroup
          label={t('settings.companyProfile.techPlatforms')}
          name="details.platforms"
          choices={mapTechPlatform}
        />
      )}
      {form.getValues('details.tools').length !== 0 && (
        <ChipGroup
          label={t('settings.companyProfile.techTools')}
          name="details.tools"
          choices={mapTechTool}
        />
      )}
      {form.getValues('details.languages').length !== 0 && (
        <ChipGroup
          label={t('settings.companyProfile.programmingLanguages')}
          name="details.languages"
          choices={mapTechLanguage}
        />
      )}

      <ChipGroup
        label={t('settings.companyProfile.targetMarkets')}
        name="details.geographyFocus"
        choices={mapCountry}
        translation={{ t, ns: 'enum' }}
      />
      <Stack>
        <BaseInputLabel text={t('caseStudies.projectSize')} />
        <Stack direction="row" gap={1} flexWrap="wrap">
          {form.watch('details.projectSize') && (
            <Chip sx={chipStyle} label={mapProjectSize.get(form.watch('details.projectSize'))} />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default RightColumnRenderComponent;

const ChipGroup = ({ label, name, choices, translation }: ChipGroupProps) => {
  const { watch } = useFormContext();

  const getTranslatedChoiceLabel = (value: string) =>
    translation ? translation.t(value, translation.ns ? { ns: translation.ns } : undefined) : value;

  return (
    <Stack>
      <BaseInputLabel text={label} />
      <Stack direction="row" gap={1} flexWrap="wrap">
        {watch(name)?.map((item: any) => (
          <Chip sx={chipStyle} label={getTranslatedChoiceLabel(choices.get(item) ?? '')} />
        ))}
      </Stack>
    </Stack>
  );
};

const chipStyle = {
  padding: '6px 6px',
  fontSize: '11px',
  fontWeight: 600,
  color: primaryDark['500'],
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  lineHeight: '16px',
  borderRadius: '3px',
  background: primaryDark['200'],
};
